/** TanStack Query hooks for Today + the alert inbox. Shared reads (jobs, techs, techs/live) come from core/entities. */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useData } from '@app/core/provider';
import { pollWhenDegraded, useStreamEvent } from '@app/core/stream';
import type { StreamEvent } from '@app/core/types';

export const tk = {
  summary: (date: string) => ['today', 'summary', date] as const,
  activity: ['today', 'activity'] as const,
  alerts: ['today', 'alerts'] as const,
  onboarding: ['today', 'onboarding'] as const,
};

const MONEY_OR_CALL = ['payment.succeeded', 'payment.refunded', 'call.ringing', 'call.answered', 'call.completed'];

export function useTodaySummary(date: string) {
  const { data } = useData();
  const qc = useQueryClient();
  // Coalesce bursts of money/call events into one refetch.
  const t = useRef<number | null>(null);
  useStreamEvent(MONEY_OR_CALL, () => {
    if (t.current != null) return;
    t.current = window.setTimeout(() => { t.current = null; qc.invalidateQueries({ queryKey: ['today', 'summary'] }); }, 1500);
  });
  useEffect(() => () => { if (t.current != null) clearTimeout(t.current); }, []);
  return useQuery({ queryKey: tk.summary(date), queryFn: () => data.today.summary(date), refetchInterval: pollWhenDegraded, retry: 1 });
}

/** Recent activity: one initial fetch, then every stream envelope as it arrives (deduped by id, newest first). */
export function useActivity(limit = 50) {
  const { data } = useData();
  const q = useQuery({ queryKey: tk.activity, queryFn: () => data.today.activity(limit), staleTime: Infinity, retry: 1 });
  const [live, setLive] = useState<StreamEvent[]>([]);
  useStreamEvent('*', (e) => {
    if (e.type === 'tech.location') return;
    setLive((prev) => (prev.some((x) => x.id && x.id === e.id) ? prev : [e, ...prev].slice(0, 200)));
  });
  const events = useMemo(() => {
    const seen = new Set<string>();
    return [...live, ...(q.data ?? [])].filter((e) => { const k = e.id || `${e.type}${e.at}`; if (seen.has(k)) return false; seen.add(k); return true; });
  }, [live, q.data]);
  return { events, isLoading: q.isLoading, error: q.error };
}

const ALERT_EVENTS = ['tech.alert', 'tech.stationary', 'tech.state', 'dispatch.escalated', 'sla.breached', 'job.assigned', 'job.declined', 'estimate.declined', 'ops.anomaly', 'stock.below_reorder'];

export function useInboxAlerts() {
  const { data } = useData();
  const qc = useQueryClient();
  const t = useRef<number | null>(null);
  useStreamEvent(ALERT_EVENTS, () => {
    if (t.current != null) return;
    t.current = window.setTimeout(() => { t.current = null; qc.invalidateQueries({ queryKey: tk.alerts }); }, 800);
  });
  useStreamEvent('job.status_changed', (e) => { if (e.data.to === 'MECHANIC DENIED') qc.invalidateQueries({ queryKey: tk.alerts }); });
  useEffect(() => () => { if (t.current != null) clearTimeout(t.current); }, []);
  return useQuery({ queryKey: tk.alerts, queryFn: () => data.today.alerts(), refetchInterval: () => pollWhenDegraded() || 120_000, retry: 1 });
}

export function useOnboarding(enabled: boolean) {
  const { data } = useData();
  const qc = useQueryClient();
  useStreamEvent(['onboarding.step_changed', 'tenant.live', 'tenant.status_changed'], () => qc.invalidateQueries({ queryKey: tk.onboarding }));
  return useQuery({ queryKey: tk.onboarding, queryFn: () => data.today.onboarding(), enabled, staleTime: 60_000 });
}

export function useAckAlert() {
  const { data } = useData();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { id: string; action: string; key: string }) => data.today.ackAlert(v.id, v.action, v.key),
    onSettled: () => qc.invalidateQueries({ queryKey: tk.alerts }),
  });
}

export function useCallTech() {
  const { data } = useData();
  return useMutation({ mutationFn: (v: { techId: string; jobId: string | null; key: string }) => data.today.callTech(v.techId, v.jobId, v.key) });
}

export function useMarkBreak() {
  const { data } = useData();
  return useMutation({ mutationFn: (v: { techId: string; key: string }) => data.today.markBreak(v.techId, v.key) });
}
